<template>
  <div class="chart-box">
    <svg :viewBox="`0 0 ${W} ${H}`" class="chart" @mousemove="onMove" @mouseleave="hover = null">
      <defs>
        <pattern :id="hatchId" width="7" height="7" patternTransform="rotate(45)" patternUnits="userSpaceOnUse">
          <line x1="0" y1="0" x2="0" y2="7" class="hatch-line" />
        </pattern>
      </defs>
      <!-- hatched plot area -->
      <rect :x="PAD.l" :y="PAD.t" :width="W - PAD.l - PAD.r" :height="H - PAD.t - PAD.b" :fill="`url(#${hatchId})`" />
      <!-- grid + y ticks -->
      <g v-for="t in yTicks" :key="`g${t}`">
        <line :x1="PAD.l" :x2="W - PAD.r" :y1="y(t)" :y2="y(t)" class="grid" />
        <text :x="PAD.l - 8" :y="y(t) + 3.5" class="tick" text-anchor="end">{{ Math.round(t * 100) }}%</text>
      </g>
      <!-- benchmark & ceiling -->
      <line :x1="PAD.l" :x2="W - PAD.r" :y1="y(benchmark)" :y2="y(benchmark)" class="benchmark" />
      <text :x="PAD.l + 6" :y="y(benchmark) + 13" class="ref-label">Benchmark 65%</text>
      <line :x1="PAD.l" :x2="W - PAD.r" :y1="y(ceiling)" :y2="y(ceiling)" class="ceiling" />
      <text :x="PAD.l + 6" :y="y(ceiling) - 5" class="ref-label">Ceiling 70%</text>
      <!-- decision year -->
      <line :x1="x(2027)" :x2="x(2027)" :y1="PAD.t" :y2="H - PAD.b" class="decision-line" />
      <!-- future scale steps (chart-only, not agreed) -->
      <g v-for="st in visibleSteps" :key="st.key">
        <line :x1="x(st.year)" :x2="x(st.year)" :y1="PAD.t + 8" :y2="H - PAD.b" class="step-line" />
        <text :x="x(st.year) + 5" :y="H - PAD.b - 8" class="step-label">{{ st.label }} +{{ st.pct }}%</text>
      </g>
      <!-- series: solid to 2027, dashed projection after -->
      <path :d="solidPath" class="series" fill="none" />
      <path v-if="dashedPath" :d="dashedPath" class="series-proj" fill="none" />
      <circle
        v-for="r in rows" :key="r.year"
        :cx="x(r.year)" :cy="y(r.staffPct)" :r="r.year === 2027 ? 4.5 : 2.6"
        :class="r.year === 2027 ? 'dot-decision' : 'dot'"
      />
      <!-- 2027 annotation -->
      <text :x="x(2027) + 10" :y="y(decisionRow.staffPct) - 14" class="anno-strong">
        {{ (decisionRow.staffPct * 100).toFixed(1) }}% — 2027 at these levers
      </text>
      <text :x="x(2027) + 10" :y="y(decisionRow.staffPct) - 2" class="anno-sub">
        recurring annual level
      </text>
      <!-- x axis -->
      <line :x1="PAD.l" :x2="W - PAD.r" :y1="H - PAD.b" :y2="H - PAD.b" class="axis" />
      <text
        v-for="r in rows" :key="`x${r.year}`" :x="x(r.year)" :y="H - PAD.b + 16"
        class="tick" :class="{ 'tick-strong': r.year === 2027 }" text-anchor="middle"
      >{{ r.year }}</text>
      <!-- hover -->
      <g v-if="hover">
        <line :x1="x(hover.year)" :x2="x(hover.year)" :y1="PAD.t" :y2="H - PAD.b" class="crosshair" />
        <circle :cx="x(hover.year)" :cy="y(hover.staffPct)" r="4.5" class="dot-hover" />
      </g>
    </svg>
    <div v-if="hover" class="tooltip" :style="tooltipStyle">
      <div class="tt-title">{{ hover.year }} <span class="tt-kind">{{ hover.kind }}</span></div>
      <div>Staff cost {{ (hover.staffPct * 100).toFixed(1) }}% of income</div>
      <div class="tt-muted">{{ fmtM(hover.staffing) }} of {{ fmtM(hover.totalIncome) }}</div>
    </div>
  </div>
</template>

<script>
import { scaleLinear, linePath, niceTicks } from '../../utils/chartUtils';
import { formatM } from '../../utils/boardEngine';

let uid = 0;

export default {
  name: 'StaffPctChart',
  props: {
    rows: { type: Array, required: true },
    benchmark: { type: Number, default: 0.65 },
    ceiling: { type: Number, default: 0.70 },
    steps: { type: Array, default: () => [] }, // [{key,label,year,pct}]
  },
  data: () => ({ W: 1000, H: 320, PAD: { l: 52, r: 24, t: 18, b: 30 }, hover: null, hatchId: `hatch-sp-${uid++}` }),
  computed: {
    x() {
      const years = this.rows.map((r) => r.year);
      return scaleLinear([Math.min(...years), Math.max(...years)], [this.PAD.l + 14, this.W - this.PAD.r - 190]);
    },
    yDomain() {
      // Stable anchors so lever changes move the LINE, not the axis; the
      // domain only stretches when the data genuinely leaves the band.
      const vals = [...this.rows.map((r) => r.staffPct), this.benchmark, this.ceiling];
      const min = Math.min(...vals), max = Math.max(...vals);
      return [Math.min(0.58, min - 0.02), Math.max(0.80, max + 0.03)];
    },
    y() { return scaleLinear(this.yDomain, [this.H - this.PAD.b, this.PAD.t]); },
    yTicks() { return niceTicks(this.yDomain[0], this.yDomain[1], 4); },
    decisionRow() { return this.rows.find((r) => r.year === 2027) || this.rows[this.rows.length - 1]; },
    visibleSteps() {
      const years = this.rows.map((r) => r.year);
      return this.steps.filter((st) => st.year >= Math.min(...years) && st.year <= Math.max(...years));
    },
    solidPath() {
      const pts = this.rows.filter((r) => r.year <= 2027).map((r) => [this.x(r.year), this.y(r.staffPct)]);
      return linePath(pts);
    },
    dashedPath() {
      const pts = this.rows.filter((r) => r.year >= 2027).map((r) => [this.x(r.year), this.y(r.staffPct)]);
      return pts.length > 1 ? linePath(pts) : '';
    },
    tooltipStyle() {
      const px = (this.x(this.hover.year) / this.W) * 100;
      return { left: `min(max(${px}%, 70px), calc(100% - 150px))`, top: '6px' };
    },
  },
  methods: {
    fmtM: formatM,
    onMove(e) {
      const rect = e.currentTarget.getBoundingClientRect();
      const vx = ((e.clientX - rect.left) / rect.width) * this.W;
      let best = null, bd = Infinity;
      for (const r of this.rows) {
        const d = Math.abs(this.x(r.year) - vx);
        if (d < bd) { bd = d; best = r; }
      }
      this.hover = best;
    },
  },
};
</script>

<style scoped>
.chart-box { position: relative; }
.chart { width: 100%; height: auto; display: block; }
.hatch-line { stroke: var(--hatch); stroke-width: 1; }
.grid { stroke: var(--hairline); stroke-width: 1; }
.axis { stroke: var(--ink); stroke-width: 1.5; }
.tick { font-size: 11px; fill: var(--muted); font-variant-numeric: tabular-nums; }
.tick-strong { fill: var(--ink); font-weight: 700; }
.benchmark { stroke: var(--ink); stroke-width: 1.5; }
.ceiling { stroke: var(--ink); stroke-width: 1; stroke-dasharray: 5 4; }
.ref-label { font-size: 11px; font-weight: 700; fill: var(--ink); }
.series { stroke: var(--ink); stroke-width: 2; stroke-linejoin: round; stroke-linecap: round; }
.series-proj { stroke: var(--red); stroke-width: 2; stroke-dasharray: 6 5; stroke-linejoin: round; }
.dot { fill: var(--ink); stroke: var(--paper); stroke-width: 2; }
.dot-decision { fill: var(--red); stroke: var(--paper); stroke-width: 2; }
.dot-hover { fill: var(--red); stroke: var(--paper); stroke-width: 2; }
.anno-strong { font-size: 14px; font-weight: 800; fill: var(--red); }
.anno-sub { font-size: 11px; fill: var(--muted); }
.decision-line { stroke: var(--ink); stroke-width: 1; stroke-dasharray: 3 4; }
.step-line { stroke: var(--red); stroke-width: 1; stroke-dasharray: 2 4; }
.step-label { font-size: 10.5px; font-weight: 700; fill: var(--red); }
.crosshair { stroke: var(--muted); stroke-width: 1; }
.tooltip {
  position: absolute; transform: translateX(-50%);
  background: var(--paper); border: 1px solid var(--ink); padding: 6px 10px;
  font-size: 12px; color: var(--ink); pointer-events: none; white-space: nowrap;
}
.tt-title { font-weight: 700; }
.tt-kind { font-weight: 400; color: var(--muted); font-size: 11px; }
.tt-muted { color: var(--muted); font-size: 11px; }
</style>
